import { useCallback, useContext, useMemo } from 'react';
import { QuoteApiModel, QuoteOptionApiModel } from '~/lib/api/smartsend/types';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import { SaveItemsAction } from '../../../lib/context/SmartSendContextModels';
import { EventSections } from '../../../lib/events/EventLogger';
import { ReAddExistingExtrasToQuoteOption } from '../../../lib/helpers/ReAddExistingExtrasToQuoteOption';
import { GenericEditProps } from './Edit';
import QuoteModal from '../quotes/QuoteModal';
import { MarketplaceService } from '../../../lib/consts/ItemMetaConstants';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import {
  CurrentSelectionType,
  DefaultQuoteRequestType,
  ToSmartSendAnalyticsModel
} from '../quotes/utils/CurrentSelectionType';
import useOnLoad from '~/routes/use-on-load';
import { isDeliveryToShopQuote } from '~/apps/smartsend/lib/helpers/IsDeliveryToShopQuote';
import { useGetClosestLocationForDelivery } from './useGetClosestLocationForDelivery';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

const loggingSection = [EventSections.SmartSend, EventSections.SmartSendItem];

export default function Service({ item, onClose }: GenericEditProps) {
  const { getClosestLocationForDelivery } = useGetClosestLocationForDelivery();
  const { getQuoteProviders, getServiceTags, updateServiceTags, getQuoteOptions } = useSmartSendApi();
  const { dispatch } = useContext(SmartSendContext);
  const { track } = useSmartSendAnalytics();

  useOnLoad(() => {
    track({
      type: 'edit_item_loaded',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.SmartSendItem]),
        action: 'service'
      }
    });
  });

  const onServiceSaveCallback = useCallback(
    (quote: QuoteApiModel, option: QuoteOptionApiModel) => {
      dispatch(
        new SaveItemsAction([item.Id], ['Service', 'ServiceOption'], {
          Service: quote.Service,
          ServiceOption: ReAddExistingExtrasToQuoteOption(item, option)
        })
      );
      onClose();
    },
    [dispatch, item, onClose]
  );

  const getQuotesCallback = useCallback(
    async (request: DefaultQuoteRequestType) => {
      const r = await getQuoteOptions(
        request.provider,
        request.accountId,
        item,
        null,
        request.includeTags,
        request.excludeTags
      );

      const output = r.success ? r.result.Quotes : null;

      // Check the shops are within proximity (Move to Edge if MVP is successful)
      const serviceIdsToHide: string[] = [];
      const shopDeliveries = output.filter((x) => isDeliveryToShopQuote(x));
      if (shopDeliveries.length > 0) {
        for (const quote of shopDeliveries) {
          const r = await getClosestLocationForDelivery(
            quote.Service.Provider,
            quote.Service.AccountId,
            quote.Service.PickUpProviderCode,
            item.Recipient.CountryIsoCode,
            item.Recipient.Postcode
          );

          if (!r) {
            serviceIdsToHide.push(quote.Service.Id);
          } else {
            quote.ClosestPickupLocation = r;
          }
        }
      }

      return output.filter((q) => !serviceIdsToHide.includes(q.Service.Id));
    },
    [getQuoteOptions, getClosestLocationForDelivery, item]
  );

  const getServiceTagsCallback = useCallback(async () => {
    const r = await getServiceTags();
    return r.success ? r.result.Tags : [];
  }, [getServiceTags]);

  const getQuoteProvidersCallback = useCallback(async () => {
    const r = await getQuoteProviders();
    return r.success ? r.result.Providers : [];
  }, [getQuoteProviders]);

  const currentSection = useMemo(() => {
    const output: CurrentSelectionType = {
      limitToAccount: false,
      accountId: item.Service?.AccountId,
      provider: item.Service?.Provider,
      id: item.Service?.Id,
      extras: item.Extras?.map((x) => x.Id),
      marketplaceService: item.Meta[MarketplaceService]?.Value,
      sender: item.Sender,
      recipient: item.Recipient,
      items: [ToSmartSendAnalyticsModel(item)]
    };
    return output;
  }, [item]);

  return (
    <QuoteModal
      getServiceTags={getServiceTagsCallback}
      updateServiceTags={updateServiceTags}
      loggingSections={loggingSection}
      quoteProviders={getQuoteProvidersCallback}
      getQuotes={getQuotesCallback}
      currentSelection={currentSection}
      onSave={onServiceSaveCallback}
      close={onClose}
    />
  );
}
